import React, { useEffect, useMemo, useState } from 'react';

const frMonth = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  } catch (_) {
    return '';
  }
};

const sortFn = (a, b, key, dir) => {
  const va = key === 'index' ? a.__index : (a[key] ?? '');
  const vb = key === 'index' ? b.__index : (b[key] ?? '');
  let res = 0;
  if (key === 'published_date') {
    res = new Date(va) - new Date(vb);
  } else {
    res = ('' + va).localeCompare('' + vb, 'fr', { sensitivity: 'base', numeric: true });
  }
  return dir === 'asc' ? res : -res;
};

export default function PublicationsDataTable({
  selectedRubrique = 'all',
  readOnly = false,
  pageLengthDefault = 100,
}) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const pageSizeOptions = [25, 50, 100, 150, 200, 250];
  const [pageSize, setPageSize] = useState(pageLengthDefault);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: 'published_date', dir: 'desc' });

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const url = new URL('/api/member/publications', API_BASE);
      if (selectedRubrique && selectedRubrique !== 'all') {
        url.searchParams.set('rubrique_id', selectedRubrique);
      }
      const headers = { 'Content-Type': 'application/json' };
      const token = (typeof window !== 'undefined') ? (localStorage.getItem('memberToken') || localStorage.getItem('authToken')) : null;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url.toString(), { headers });
      if (!res.ok) throw new Error(`Requête échouée (${res.status})`);
      const data = await res.json();
      
      // Utiliser le domaine courant pour les URLs de fichiers (évite les problèmes de redirection en production)
      const fileBaseUrl = typeof window !== 'undefined' ? window.location.origin : API_BASE;
      
      const items = (Array.isArray(data) ? data : []).map((p, idx) => {
        let fullFileUrl = null;
        if (p.full_file_url) {
          fullFileUrl = p.full_file_url.startsWith('http') 
            ? p.full_file_url 
            : `${fileBaseUrl}${p.full_file_url}`;
        } else if (p.file_url) {
          // Utiliser file_url si disponible
          fullFileUrl = p.file_url.startsWith('http') 
            ? p.file_url 
            : `${fileBaseUrl}/api/uploads/${p.file_url}`;
        } else if (p.file_path) {
          fullFileUrl = `${fileBaseUrl}/api/uploads/${p.file_path}`;
        }
        
        return {
          __index: idx + 1,
          id: p.id,
          title: p.title,
          rubrique_name: p.rubrique_name || '',
          published_date: p.published_date,
          file_url: p.file_url || p.file_path,
          full_file_url: fullFileUrl,
        };
      });
      setRows(items);
      setPage(1);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedRubrique]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter(r => (
        (r.title || '').toLowerCase().includes(q) ||
        (r.rubrique_name || '').toLowerCase().includes(q) ||
        frMonth(r.published_date).toLowerCase().includes(q)
      ));
    }
    const s = [...list].sort((a,b) => sortFn(a,b, sort.key, sort.dir));
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return {
      total: s.length,
      pageRows: s.slice(start, end).map((r, i) => ({ ...r, __rownum: start + i + 1 })),
    };
  }, [rows, search, page, pageSize, sort]);

  const toggleSort = (key) => {
    setPage(1);
    setSort(prev => prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' });
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="flex items-center justify-between p-4">
        <div className="text-sm flex items-center space-x-2" style={{ fontFamily: '"Lora", serif' }}>
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="border rounded px-2 py-1"
          >
            {pageSizeOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <span>entries</span>
        </div>
        <div className="text-sm" style={{ fontFamily: '"Lora", serif' }}>
          <label className="mr-2">Search:</label>
          <input
            value={search}
            onChange={(e)=> { setSearch(e.target.value); setPage(1); }}
            className="border rounded px-2 py-1"
            placeholder="Rechercher..."
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-t" style={{ minWidth: '900px' }}>
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: 'index', label: 'N°', width: '6rem' },
                { key: 'title', label: 'Titre' },
                { key: 'rubrique_name', label: 'Catégorie', width: '14rem' },
                { key: 'published_date', label: 'Date', width: '12rem' },
                { key: 'actions', label: 'Actions', width: '16rem', nosort: true },
              ].map(col => (
                <th key={col.key}
                    style={{ fontFamily: '"Lora", serif' }}
                    className={`text-left text-sm font-semibold text-gray-700 px-4 py-3 border-b`}
                >
                  <div className="flex items-center cursor-pointer select-none" onClick={() => !col.nosort && toggleSort(col.key)}>
                    <span>{col.label}</span>
                    {!col.nosort && (
                      <span className="ml-1 text-xs text-gray-400">
                        {sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={5}>Chargement...</td></tr>
            )}
            {(!loading && error) && (
              <tr><td className="px-4 py-6 text-center text-red-600" colSpan={5}>{error}</td></tr>
            )}
            {(!loading && !error && filtered.total === 0) && (
              <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={5}>Aucun résultat</td></tr>
            )}
            {(!loading && !error) && filtered.pageRows.map((r) => (
              <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700 border-b" style={{ fontFamily: '"Lora", serif' }}>{r.__rownum}</td>
                <td className="px-4 py-3 text-gray-900 border-b" style={{ fontFamily: '"Lora", serif' }}>{r.title}</td>
                <td className="px-4 py-3 text-gray-700 border-b" style={{ fontFamily: '"Lora", serif' }}>{r.rubrique_name}</td>
                <td className="px-4 py-3 text-gray-700 border-b" style={{ fontFamily: '"Lora", serif' }}>{frMonth(r.published_date)}</td>
                <td className="px-4 py-3 border-b">
                  <div className="flex items-center gap-2" style={{ fontFamily: '"Lora", serif' }}>
                    <a
                      href={r.full_file_url || '#'}
                      target={r.full_file_url ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className={`publication-action-btn px-3 py-1 text-white rounded ${r.full_file_url ? '' : 'opacity-50 pointer-events-none'}`}
                      style={{ 
                        backgroundColor: 'rgb(196, 150, 96)',
                        display: 'inline-block',
                        visibility: 'visible',
                        opacity: r.full_file_url ? 1 : 0.5
                      }}
                    >
                      Consulter
                    </a>
                    <a
                      href={r.full_file_url ? r.full_file_url.replace('/api/files/', '/api/files/').replace(/\/([^/]+)$/, '/$1/download') : '#'}
                      className={`publication-action-btn px-3 py-1 text-gray-700 border rounded hover:bg-gray-50 ${r.full_file_url ? '' : 'opacity-50 pointer-events-none'}`}
                      style={{
                        display: 'inline-block',
                        visibility: 'visible',
                        opacity: r.full_file_url ? 1 : 0.5
                      }}
                    >
                      Télécharger
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between p-4 text-sm" style={{ fontFamily: '"Lora", serif' }}>
        <div>
          {filtered.total > 0 && (
            <span>
              Affichage {Math.min((page-1)*pageSize+1, filtered.total)} à {Math.min(page*pageSize, filtered.total)} sur {filtered.total} entrées
            </span>
          )}
        </div>
        <div className="space-x-2">
          <button
            onClick={() => setPage(p => Math.max(1, p-1))}
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={page === 1}
          >
            Précédent
          </button>
          <button
            onClick={() => setPage(p => ((p*pageSize) < filtered.total ? p+1 : p))}
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={(page*pageSize) >= filtered.total}
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
}
